/**
 * Provides an object to abstract the construction of URL parameters used when filtering requests
 * for model/collection data.
 */
import _ from 'underscore';

/**
 * Contains an 'enumeration' of all the possible operations supported by the Filter object.  The text values of the
 * object's properties are chosen such that they match format expected by the server (when applicable).
 */
const Operation = {
  OR: 'or',
  AND: 'and',
  CONTAINS: '~',
  IN: 'IN',
  DOES_NOT_CONTAIN: '!~',
  EXACTLY: '=',
  GREATER_THAN: '>',
  GREATER_THAN_EQUAL_TO: '>=',
  LESS_THAN: '<',
  LESS_THAN_EQUAL_TO: '<=',
  NOT_EQUAL_TO: '!=',
  IN_RANGE: '><',
  SORT_BY: 'orderby',
  LIMIT_TO: 'limit',
  OFFSET: 'offset',
  LOAD_RELATIONS: 'load_relations',
  ARCHIVED: 'archived',
  READONLY: 'readonly',
};

/**
 * Container class for a filter operation.
 *
 * @param operation     The operation for the filter.  Should be one of the types defined in the Operation type
 * @param field         The field of the operation.  Usually this will map to the parameter name sent along with
 *                      the request; however, for some operations (SORT_BY, LIMIT_TO, OFFSET, OR) the parameter name
 *                      will instead be set to operation text.
 * @param value         The value to applied for the filter.  For some operations (IN, IN_RANGE, OR) this will be an
 *                      array.
 * @constructor
 */
class FilterOperation {
  constructor(operation, field, value) {
    this.operation = operation;

    if (!_.isUndefined(field)) {
      this.field = field;
    }

    if (!_.isUndefined(value)) {
      this.value = value;
    }
  }
}

/**
 * Helper function that will return an operation formatted for use as a query parameter.  The expected format for
 * each type of operation is documented at http://confluence.atlightspeed.net/pages/viewpage.action?pageId=21530021.
 *
 * @param filterOperation   A FilterOperation object that is to be converted into a format suitable for submitting
 *                          with a request.
 * @returns {Object}        An object with a single key/value pair.  The key supplied is intended to be used as
 *                          a URL parameter to be submitted with a Cloud API GET request.  The value is the value
 *                          of the parameter in a format expected by the server.
 */
function convertParameter(filterOperation) {
  var queryParam = {};

  switch (filterOperation.operation) {
    case Operation.CONTAINS:
    case Operation.DOES_NOT_CONTAIN:
    case Operation.GREATER_THAN:
    case Operation.GREATER_THAN_EQUAL_TO:
    case Operation.LESS_THAN:
    case Operation.LESS_THAN_EQUAL_TO:
    case Operation.NOT_EQUAL_TO:
      queryParam[filterOperation.field] = `${filterOperation.operation},${filterOperation.value}`;
      break;

    case Operation.IN:
    case Operation.IN_RANGE:
      queryParam[filterOperation.field] = `${
        filterOperation.operation
      },${filterOperation.value.join(',')}`;
      break;

    case Operation.SORT_BY:
      queryParam[filterOperation.operation] = filterOperation.field;
      if (filterOperation.value) {
        queryParam.orderby_desc = 1;
      }
      break;

    case Operation.LIMIT_TO:
    case Operation.OFFSET:
    case Operation.LOAD_RELATIONS:
    case Operation.ARCHIVED:
    case Operation.READONLY:
      queryParam[filterOperation.operation] = filterOperation.value;
      break;

    case Operation.OR:
      var i;
      var composedQuery = '';
      for (i = 0; i < filterOperation.value.length; i++) {
        var convertedParameter = convertParameter(filterOperation.value[i]);

        if (!_.isEmpty(convertedParameter)) {
          composedQuery += `${_.keys(convertedParameter)[0]}=${encodeURIComponent(
            _.values(convertedParameter)[0]
          )}`;
          composedQuery += i < filterOperation.value.length - 1 ? '|' : '';
        }
      }
      queryParam[filterOperation.operation] = composedQuery;
      break;

    case Operation.EXACTLY:
      // no special characters required; use the value directly
      queryParam[filterOperation.field] = filterOperation.value;
      break;

    default:
      break;
  }

  return queryParam;
}

/**
 * Helper function that invokes convertParameter() for each filter operation contained in a specified filter.
 *
 * @param filter        The filter to be converted into query parameter => query value pairs.
 * @returns {Object}    An object of the form:
 *                          { param1 : formattedFilterOperation,
 *                            param2 : formattedFilterOperation,
 *                            ...
 *                          }
 *
 *                      The returned object is intended to be used as data passed into Backbone.Mode.fetch().
 */
function convertQueryParameters(filter) {
  var queryParams = {};

  var i;
  for (i = 0; i < filter._filter.length; i++) {
    var convertedParameter = convertParameter(filter._filter[i]);
    var fieldIntersect = _.intersection(_.keys(convertedParameter), _.keys(queryParams));
    if (fieldIntersect.length) {
      // multiple AND criteria against the same field is not supported by the current Cloud API so warn
      // about the problem and ignore this filter operation
      console.warn(
        'The field(s)',
        fieldIntersect,
        'already have a filter applied.  Ignoring filter: ',
        filter._filter[i]
      );
    } else {
      _.extend(queryParams, convertedParameter);
    }
  }

  return queryParams;
}

export default class Filter {
  /**
   * The Filter object.  Maintains a list of all filter operations that have been applied and a facility for
   * converting all applied operations into a parameter list suitable for use by Backbone.Model.fetch().
   *
   * @constructor
   */
  constructor() {
    this._filter = []; // the filter operations list
  }

  /**
   * Add an operation to retrieve records that contain the specified string or values for the specified field.
   *
   * @param field         The model field to apply the operation against.
   * @param value         The value to be matched.  May be a string or an array.  If it's a string then a
   *                      CONTAINS operation will be added to the filter.  If it's an array then an IN operation will
   *                      be added.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  contains(field, value) {
    if (field) {
      if (_.isString(value)) {
        // value is a string so apply a CONTAINS filter
        this._filter.push(new FilterOperation(Operation.CONTAINS, field, `%${value}%`));
      } else if (_.isArray(value)) {
        // value is an array (a set of values), so use an IN filter
        this._filter.push(new FilterOperation(Operation.IN, field, value));
      } else {
        // not valid for any other type of operation
        console.error(
          'A contains filter cannot be applied to [',
          value,
          '] which is of type',
          typeof value
        );
      }
    } else {
      console.error('The field must be specified in call to contains(): field =>', field);
    }

    return this;
  }

  /**
   * Add an operation to filter out records that contain a specified string for the specified field.
   *
   * @param field         The model field to apply the operation against.
   * @param value         The string to be used in the filter operation.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  doesNotContain(field, value) {
    if (field) {
      if (_.isString(value)) {
        // value is a string so apply a CONTAINS filter
        this._filter.push(new FilterOperation(Operation.DOES_NOT_CONTAIN, field, `%${value}%`));
      } else {
        // not valid for any other type of operation
        console.error(
          'A does not contains filter cannot be applied to [',
          value,
          '] which is of type',
          typeof value
        );
      }
    } else {
      console.error('The field must be specified in call to doesNotContain(): field =>', field);
    }

    return this;
  }

  /**
   * Add an operation to filter out records that do not contain a specified value for the specified field.
   *
   * @param field         The model field to apply the operation against.
   * @param value         The value to be matched as part of the operation.  Valid types are: strings, numbers, dates
   *                      and booleans.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  exactly(field, value) {
    if (field) {
      // let's only use the exactly filter when it would make sense to a user
      if (_.isString(value) || _.isNumber(value) || _.isDate(value) || _.isBoolean(value)) {
        this._filter.push(new FilterOperation(Operation.EXACTLY, field, value));
      } else {
        // not valid for any other type of operation
        console.error(
          'An exactly filter cannot be applied to [',
          value,
          '] which is of type',
          typeof value
        );
      }
    } else {
      console.error('The field must be specified in call to exactly(): field =>', field);
    }

    return this;
  }

  /**
   * Add an operation to filter out records that contain values less than
   * or equal to the specified value for the specified field.
   *
   * @param field         The model field to apply the operation against.
   * @param value         The value to use during the comparison.  The value specified must be a number or date.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  greaterThan(field, value) {
    if (field) {
      // although the back-end (since it uses SQL) supports greater than operations on strings lets limit this
      // just to numbers for implementation since in typical use greater than doesn't make sense to a user when
      // filtering against a string
      if (_.isNumber(value) || _.isDate(value)) {
        this._filter.push(new FilterOperation(Operation.GREATER_THAN, field, value));
      } else {
        // not valid for any other type of operation
        console.error(
          'A greater than filter cannot be applied to [',
          value,
          '] which is of type',
          typeof value
        );
      }
    } else {
      console.error('The field must be specified in call to greaterThan(): field =>', field);
    }

    return this;
  }

  /**
   * Add an operation to filter out records that contain values less than the specified value for the specified field.
   *
   * @param field         The model field to apply the operation against.
   * @param value         The value to use during the comparison.  The value specified must be a number or date.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  greaterThanEqualTo(field, value) {
    if (field) {
      // although the back-end (since it uses SQL) supports greater than operations on strings lets limit this
      // just to numbers for implementation since in typical use greater than doesn't make sense to a user when
      // filtering against a string
      if (_.isNumber(value) || _.isDate(value)) {
        this._filter.push(new FilterOperation(Operation.GREATER_THAN_EQUAL_TO, field, value));
      } else {
        // not valid for any other type of operation
        console.error(
          'A greater than or equal to filter cannot be applied to [',
          value,
          '] which is of type',
          typeof value
        );
      }
    } else {
      console.error('The field must be specified in call to greaterThanEqualTo(): field =>', field);
    }

    return this;
  }

  /**
   * Add an operation to filter out records that contain values greater than
   * or equal to the specified value for the specified field.
   *
   * @param field         The model field to apply the operation against.
   * @param value         The value to use during the comparison.  The value specified must be a number or date.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  lessThan(field, value) {
    if (field) {
      // although the back-end (since it uses SQL) supports less than operations on strings lets limit this
      // just to numbers for implementation since in typical use less than doesn't make sense to a user when
      // filtering against a string
      if (_.isNumber(value) || _.isDate(value)) {
        this._filter.push(new FilterOperation(Operation.LESS_THAN, field, value));
      } else {
        // not valid for any other type of operation
        console.error(
          'A less than filter cannot be applied to [',
          value,
          '] which is of type',
          typeof value
        );
      }
    } else {
      console.error('The field must be specified in call to lessThan(): field =>', field);
    }

    return this;
  }

  /**
   * Add an operation to filter out records that contain values greater
   * than the specified value for the specified field.
   *
   * @param field         The model field to apply the operation against.
   * @param value         The value to use during the comparison.  The value specified must be a number or date.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  lessThanEqualTo(field, value) {
    if (field) {
      // although the back-end (since it uses SQL) supports less than operations on strings lets limit this
      // just to numbers for implementation since in typical use less than doesn't make sense to a user when
      // filtering against a string
      if (_.isNumber(value) || _.isDate(value)) {
        this._filter.push(new FilterOperation(Operation.LESS_THAN_EQUAL_TO, field, value));
      } else {
        // not valid for any other type of operation
        console.error(
          'A less than or equal to filter cannot be applied to [',
          value,
          '] which is of type',
          typeof value
        );
      }
    } else {
      console.error('The field must be specified in call to lessThanEqualTo(): field =>', field);
    }

    return this;
  }

  /**
   * Add an operation to filter out records that contain a specified value for the specified field.
   *
   * @param field         The model field to apply the operation against.
   * @param value         The value to be matched as part of the operation.  Valid types are: strings, numbers, dates
   *                      and booleans.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  notEqual(field, value) {
    if (field) {
      // valid for any primitive type
      if (_.isString(value) || _.isNumber(value) || _.isDate(value) || _.isBoolean(value)) {
        this._filter.push(new FilterOperation(Operation.NOT_EQUAL_TO, field, value));
      } else {
        // not valid for any other type of operation
        console.error(
          'A not equal filter cannot be applied to [',
          value,
          '] which is of type',
          typeof value
        );
      }
    } else {
      console.error('The field must be specified in call to notEqual(): field =>', field);
    }

    return this;
  }

  /**
   * Add an operation to filter out records where the specified field is outside the specified range.
   *
   * @param field         The model field to apply the operation against.
   * @param value1        The first to be matched as part of the operation.  Must be a number or a date.
   * @param value2        The second to be matched as part of the operation.  Must be a number or a date.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  inRange(field, value1, value2) {
    if (field) {
      if ((_.isNumber(value1) || _.isDate(value1)) && (_.isNumber(value2) || _.isDate(value2))) {
        if (typeof value1 === typeof value2) {
          this._filter.push(new FilterOperation(Operation.IN_RANGE, field, [value1, value2]));
        } else {
          console.error(
            'The values specified for the in range filter are of different types:',
            `value1 : ${value1}`,
            `, value2 : ${value2}`
          );
        }
      } else {
        // not valid for any other type of operation
        console.error(
          'An in range filter cannot be applied to [',
          `${value1}, ${value2}`,
          '] which are of types [',
          `${typeof value1}, ${value2}`,
          ']'
        );
      }
    } else {
      console.error('The field must be specified in call to inRange(): field =>', field);
    }

    return this;
  }

  /**
   * Add an operation to sort the records ordered by the specified field.  The default is to sort in ascending order.
   *
   * @param field         The model field to apply the operation against.
   * @param descending    When true, the records will be sorted in descending order.
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  sortBy(field, descending) {
    descending = descending || false;

    if (_.isString(field)) {
      this._filter.push(new FilterOperation(Operation.SORT_BY, field, descending));
    } else {
      console.error(
        'A sort by filter cannot be applied to [',
        field,
        '] which is of type',
        typeof field
      );
    }

    return this;
  }

  /**
   * Add an operation to limit the records retrieved by a request.
   *
   * @param numOfRecords         The maximum number of records to be retrieved.
   * @returns {Filter}           The updated (potentially) Filter object.
   */
  limitTo(numOfRecords) {
    if (_.isNumber(numOfRecords)) {
      this._filter.push(new FilterOperation(Operation.LIMIT_TO, null, numOfRecords));
    } else {
      console.error(
        'A limit to filter cannot be applied to [',
        numOfRecords,
        '] which is of type',
        typeof numOfRecords
      );
    }

    return this;
  }

  /**
   * Add an operation to specify the offset into the list of records to retrieve with a request.
   *
   * @param numOfRecords         The maximum number of records to be retrieved.
   * @returns {Filter}           The updated (potentially) Filter object.
   */
  offset(value) {
    if (_.isNumber(value)) {
      this._filter.push(new FilterOperation(Operation.OFFSET, null, value));
    } else {
      console.error(
        'An offset filter cannot be applied with [',
        value,
        '] which is of type',
        typeof value
      );
    }

    return this;
  }

  /**
   * Sets the operation that specifies the relations to retrieve with a request.
   *
   * @param relations         Either i)an array of stringss indicating the relations to load or ii) the string 'all'
   * @returns {Filter}           The updated (potentially) Filter object.
   */
  loadRelations(relations) {
    if (_.isArray(relations) || relations === 'all') {
      // update if already present
      let reln = _.findWhere(this._filter, { operation: 'load_relations' });
      if (reln) {
        reln.value = _.isArray(relations) ? JSON.stringify(relations) : relations;
      } else {
        let relationsAsString = relations === 'all' ? relations : JSON.stringify(relations);
        this._filter.push(new FilterOperation(Operation.LOAD_RELATIONS, null, relationsAsString));
      }
    } else {
      console.error(
        'A load_relations filter cannot be applied with [',
        relations,
        '] which is of type',
        typeof relations
      );
    }

    return this;
  }

  /**
   * Sets the operation that specifies whether archived values should be returned in the query result
   *
   * @param value         1/0/only/true/false
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  archived(value) {
    if (value !== 'only' && value !== '0' && value !== '1') {
      value = value ? '1' : '0';
    }
    // update if already present
    let reln = _.findWhere(this._filter, { operation: 'archived' });
    if (reln) {
      reln.value = value;
    } else {
      this._filter.push(new FilterOperation(Operation.ARCHIVED, null, value));
    }
    return this;
  }

  /**
   * Sets whether GET requests will be using the readonly backend.
   * Set to false only if you need to read data immediately after writing it
   *
   * @param value {boolean} true to avoid replication delay between this read and when it was written
   */
  readonly(value) {
    value = !!value;

    // update if already present
    let reln = _.findWhere(this._filter, { operation: 'readonly' });
    if (reln) {
      reln.value = value;
    } else {
      this._filter.push(new FilterOperation(Operation.READONLY, null, value));
    }
    return this;
  }

  /**
   * Concatenate two filters together.
   *
   * @param filter        The filter to be concatenated with this one.
   * @returns {Filter}    The concatenated Filter object.
   */
  and(filter) {
    if (filter instanceof Filter) {
      this._filter = this._filter.concat(filter._filter);
    } else {
      console.error(
        'An and operation cannot be applied with [',
        filter,
        '] which is of type',
        typeof filter
      );
    }

    return this;
  }

  /**
   * Apply an OR operation to all operations in a specified filter and add it to this one.
   *
   * @param filter        The filter that will be OR'd
   * @returns {Filter}    The updated (potentially) Filter object.
   */
  or(filter) {
    if (filter instanceof Filter) {
      this._filter.push(new FilterOperation(Operation.OR, Operation.OR, filter._filter));
    } else {
      console.error(
        'An or operation cannot be applied with [',
        filter,
        '] which is of type',
        typeof filter
      );
    }

    return this;
  }

  /**
   * Get a key => value mapping of all operations applied to the filter in a format suitable for transmission to
   * the server.
   *
   * @returns {Object}    An object of the form:
   *                          { param1 : formattedFilterOperation,
   *                            param2 : formattedFilterOperation,
   *                            ...
   *                          }
   *
   *                      The returned object is intended to be used as data passed into Backbone.Mode.fetch().
   */
  getQueryParameters() {
    return convertQueryParameters(this);
  }
}
